import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { HeaderComponent, SidebarComponent, FooterComponent } from '@pragma/components';
import { LayoutService } from '@pragma/services';
import { filter, Subscription } from 'rxjs';

@Component({
    standalone: true,
    selector: 'ngx-one-column-left-layout',
    template: `
        <div class="layout-wrapper" [ngClass]="containerClass">
            <ngx-header></ngx-header>
            <div class="layout-sidebar">
                <ngx-sidebar></ngx-sidebar>
                <div class="layout-sidebar-footer">
                    <ngx-footer></ngx-footer>
                </div>
            </div>
            <div class="layout-main-container">
                <div class="layout-main">
                    <ng-content select="router-outlet"></ng-content>
                </div>
                <!-- <ngx-footer></ngx-footer> -->
            </div>
            <!-- <app-config></app-config> -->
            <div class="layout-mask"></div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, NgClass, RouterModule, HeaderComponent, FooterComponent, SidebarComponent]
})
export class OneColumnLeftLayoutComponent implements OnDestroy {
    overlayMenuOpenSubscription: Subscription;

    menuOutsideClickListener: any;

    @ViewChild(SidebarComponent) appSidebar!: SidebarComponent;

    @ViewChild(HeaderComponent) appTopbar!: HeaderComponent;
    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        private _cd: ChangeDetectorRef
    ) {
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    const isOutsideClicked = !(
                        this.appSidebar.el.nativeElement.isSameNode(event.target) ||
                        this.appSidebar.el.nativeElement.contains(event.target) ||
                        this.appTopbar.menuButton.nativeElement.isSameNode(event.target) ||
                        this.appTopbar.menuButton.nativeElement.contains(event.target)
                    );

                    if (isOutsideClicked) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.state().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.hideMenu();
        });
    }

    hideMenu() {
        this.layoutService.state().overlayMenuActive = false;
        this.layoutService.state().staticMenuMobileActive = false;
        this.layoutService.state().menuHoverActive = false;
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
            this._cd.markForCheck();
        }
        this.unblockBodyScroll();
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(
                new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
                ' '
            );
        }
    }

    get containerClass() {
        return {
            'layout-theme-light': this.layoutService.config().colorScheme === 'light',
            'layout-theme-dark': this.layoutService.config().colorScheme === 'dark',
            'layout-overlay': this.layoutService.config().menuMode === 'overlay',
            'layout-static': this.layoutService.config().menuMode === 'static',
            'layout-static-inactive':
                this.layoutService.state().staticMenuDesktopInactive &&
                this.layoutService.config().menuMode === 'static',
            'layout-overlay-active': this.layoutService.state().overlayMenuActive,
            'layout-mobile-active': this.layoutService.state().staticMenuMobileActive,
            'p-input-filled': this.layoutService.config().inputStyle === 'filled',
            'p-ripple-disabled': !this.layoutService.config().ripple
        };
    }

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }
}
